import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
    Card,
    CardBody,
    Typography,
    Button,
    Dialog,
    DialogHeader,
    DialogBody,
    DialogFooter,
    Checkbox,
} from '@material-tailwind/react';
import ReactSelect from 'react-select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faRefresh,
    faFileInvoiceDollar,
    faRotateLeft,
    faTimes,
} from '@fortawesome/free-solid-svg-icons';
import { rootUrl } from '../../../config/config';
import { generateMultipleInvoices } from '../../../utils/jobInvoicePDFGenerator';
import { fetchCompanyInfo } from '../../../utils/companyInfo';
import ReverseTransactionDialog from '../owners/ReverseTransactionDialog';

const JobCardInvoicing = () => {
    const token = localStorage.getItem('authToken') || '';
    const navigate = useNavigate();

    // State Management
    const [activeTab, setActiveTab] = useState('uninvoiced'); // 'uninvoiced' | 'posted'
    const [loading, setLoading] = useState(false);
    const [jobCards, setJobCards] = useState([]);
    const [postedInvoices, setPostedInvoices] = useState([]);
    const [selectedCardIds, setSelectedCardIds] = useState(new Set());
    const [ownerFilter, setOwnerFilter] = useState(null);
    const [owners, setOwners] = useState([]);
    const [invoiceableStatus, setInvoiceableStatus] = useState('AWAITING_INVOICE');
    const [selectedWeek, setSelectedWeek] = useState(null);
    const [availableWeeks, setAvailableWeeks] = useState([]);
    const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().split('T')[0]);

    // Modal States
    const [showResultsModal, setShowResultsModal] = useState(false);
    const [invoiceResults, setInvoiceResults] = useState([]);
    const [reverseDialogOpen, setReverseDialogOpen] = useState(false);
    const [selectedTransaction, setSelectedTransaction] = useState(null);

    // Fetch invoiceable status from system settings
    useEffect(() => {
        const fetchStatus = async () => {
            try {
                const response = await axios.get(
                    `${rootUrl}/system/jc_invoiceable_status`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                if (response.data && response.data.value) {
                    setInvoiceableStatus(response.data.value);
                }
            } catch (err) {
                console.error('Error fetching invoiceable status:', err);
                // Default to AWAITING_INVOICE
            }
        };
        fetchStatus();
    }, [token]);

    // Fetch available weeks
    useEffect(() => {
        const fetchWeeks = async () => {
            try {
                const response = await axios.get(
                    `${rootUrl}/system/weeks`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                if (response.data && response.data.success && response.data.data) {
                    setAvailableWeeks(response.data.data);
                    // Set default to first (current) week
                    if (response.data.data.length > 0) {
                        setSelectedWeek(response.data.data[0].week);
                    }
                }
            } catch (err) {
                console.error('Error fetching weeks:', err);
            }
        };
        fetchWeeks();
    }, [token]);

    // Fetch owners
    useEffect(() => {
        const fetchOwners = async () => {
            try {
                const response = await axios.get(
                    `${rootUrl}/owners`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                if (response.data && response.data.success) {
                    const ownerOptions = response.data.data.map(o => ({
                        value: o.code,
                        label: `${o.code} - ${o.name}`
                    }));
                    setOwners(ownerOptions);
                }
            } catch (err) {
                console.error('Error fetching owners:', err);
            }
        };
        fetchOwners();
    }, [token]);

    // Fetch uninvoiced job cards
    const fetchUninvoicedCards = useCallback(async () => {
        setLoading(true);
        try {
            let url = `${rootUrl}/api/job-cards?status=${invoiceableStatus}`;
            if (ownerFilter) {
                url += `&owner_code=${ownerFilter.value}`;
            }

            const response = await axios.get(url, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data && response.data.success) {
                setJobCards(response.data.data || []);
                setSelectedCardIds(new Set()); // Clear selection
            }
        } catch (err) {
            console.error('Error fetching uninvoiced cards:', err);
        } finally {
            setLoading(false);
        }
    }, [invoiceableStatus, ownerFilter, token]);

    // Fetch posted invoices (JIN transactions)
    const fetchPostedInvoices = useCallback(async () => {
        setLoading(true);
        try {
            let url = `${rootUrl}/api/owners/transactions?trans_type=JIN`;
            if (selectedWeek) {
                url += `&week_key=${selectedWeek}`;
            }
            const response = await axios.get(url, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data && response.data.success) {
                setPostedInvoices(response.data.data || []);
            }
        } catch (err) {
            console.error('Error fetching posted invoices:', err);
        } finally {
            setLoading(false);
        }
    }, [selectedWeek, token]);

    // Load data on tab change or filter change
    useEffect(() => {
        if (activeTab === 'uninvoiced') {
            fetchUninvoicedCards();
        } else if (activeTab === 'posted') {
            fetchPostedInvoices();
        }
    }, [activeTab, fetchUninvoicedCards, fetchPostedInvoices]);

    // Handle card selection
    const toggleCardSelection = (cardId) => {
        const newSelection = new Set(selectedCardIds);
        if (newSelection.has(cardId)) {
            newSelection.delete(cardId);
        } else {
            newSelection.add(cardId);
        }
        setSelectedCardIds(newSelection);
    };

    // Handle select all
    const toggleSelectAll = () => {
        if (selectedCardIds.size === jobCards.length) {
            setSelectedCardIds(new Set());
        } else {
            setSelectedCardIds(new Set(jobCards.map(jc => jc.id)));
        }
    };

    // Handle reverse transaction
    const handleReverseClick = (transaction) => {
        setSelectedTransaction(transaction);
        setReverseDialogOpen(true);
    };

    // Handle successful reversal
    const handleReverseSuccess = async () => {
        setReverseDialogOpen(false);
        setSelectedTransaction(null);
        // Refresh both lists
        await fetchPostedInvoices();
        await fetchUninvoicedCards();
        // Switch to uninvoiced tab to show reverted card
        setActiveTab('uninvoiced');
    };



    // Calculate grouped summary
    const getGroupedSummary = () => {
        const grouped = {};
        Array.from(selectedCardIds).forEach(cardId => {
            const card = jobCards.find(jc => jc.id === cardId);
            if (card) {
                if (!grouped[card.owner_code]) {
                    grouped[card.owner_code] = { count: 0, total: 0, ownerName: card.owner_name };
                }
                grouped[card.owner_code].count += 1;
                grouped[card.owner_code].total += parseFloat(card.total_cost || 0);
            }
        });
        return grouped;
    };

    // Handle bulk invoice with exponential backoff
    const handleBulkInvoice = async () => {
        if (selectedCardIds.size === 0) {
            alert('Please select at least one job card');
            return;
        }

        const MAX_RETRIES = 3;
        const RETRY_DELAYS = [1000, 2000, 4000]; // 1s, 2s, 4s delays

        const invoiceWithRetry = async (retryCount = 0) => {
            setLoading(true);
            try {
                const response = await axios.post(
                    `${rootUrl}/api/job-cards/bulk-invoice`,
                    {
                        jobCardIds: Array.from(selectedCardIds),
                        week: selectedWeek,
                        invoiceDate: invoiceDate
                    },
                    { headers: { Authorization: `Bearer ${token}` } }
                );

                if (response.data && response.data.success !== undefined) {
                    const results = response.data.results || [];
                    setInvoiceResults(results);

                    // Generate PDFs sequentially for successful invoices
                    const successfulInvoices = results.filter(r => r.success);
                    if (successfulInvoices.length > 0) {
                        const cardsToInvoice = jobCards.filter(jc =>
                            successfulInvoices.some(r => r.job_card_id === jc.id)
                        );

                        // Get owner data for PDF
                        const ownerCode = successfulInvoices[0].owner_code;
                        const ownerData = owners.find(o => o.value === ownerCode);
                        const companyInfo = await fetchCompanyInfo(token);

                        try {
                            await generateMultipleInvoices(
                                cardsToInvoice,
                                {
                                    name: ownerData?.label?.split(' - ')[1] || 'Unknown',
                                    code: ownerCode
                                },
                                successfulInvoices,
                                companyInfo
                            );
                        } catch (pdfErr) {
                            console.error('Error generating PDFs:', pdfErr);
                            // Continue anyway, transactions already created
                        }
                    }

                    setShowResultsModal(true);
                    fetchUninvoicedCards(); // Refresh list
                }
            } catch (err) {
                console.error('Error during bulk invoice:', err);

                // Handle 409 Conflict (lock timeout) with exponential backoff
                if (err.response?.status === 409 && retryCount < MAX_RETRIES) {
                    const nextRetryCount = retryCount + 1;
                    const delayMs = RETRY_DELAYS[retryCount];
                    alert(`Job cards being modified. Retrying... (Attempt ${nextRetryCount} of ${MAX_RETRIES})\nRetrying in ${delayMs / 1000}s`);

                    // Wait and retry
                    await new Promise(resolve => setTimeout(resolve, delayMs));
                    return invoiceWithRetry(nextRetryCount);
                }

                // For other errors or after max retries exhausted
                if (retryCount >= MAX_RETRIES && err.response?.status === 409) {
                    alert('Unable to invoice after 3 attempts. Job cards are being modified by another user. Please try again later.');
                } else {
                    alert('Error: ' + (err.response?.data?.message || err.message));
                }
            } finally {
                setLoading(false);
            }
        };

        // Start invoicing with retry logic
        invoiceWithRetry();
    };

    const groupedSummary = getGroupedSummary();
    const summaryItems = Object.entries(groupedSummary);

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA]">
            {/* Header */}
            <div className="p-6 lg:p-8">
                <div className="text-center mb-8">
                    <h1 className="text-4xl font-bold text-[#2a2e30] mb-2">Job Card Invoicing</h1>
                    <div className="w-24 h-1 bg-[#FF5733] mx-auto rounded-full mb-4"></div>
                    <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-6">
                        <div className="flex items-center gap-2 flex-1 max-w-xs">
                            <label className="text-sm font-semibold text-[#2a2e30] whitespace-nowrap">Select Week:</label>
                            <ReactSelect
                                placeholder="-- Select Week --"
                                classNamePrefix="react-select"
                                isClearable
                                options={availableWeeks.map(week => ({
                                    value: week.week,
                                    label: `${week.week} (${new Date(week.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${new Date(week.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })})`
                                }))}
                                value={selectedWeek ? {
                                    value: selectedWeek,
                                    label: (() => {
                                        const week = availableWeeks.find(w => w.week === selectedWeek);
                                        return week ? `${week.week} (${new Date(week.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${new Date(week.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })})` : selectedWeek;
                                    })()
                                } : null}
                                onChange={(option) => setSelectedWeek(option ? option.value : null)}
                            />
                        </div>
                        <div className="flex items-center gap-2">
                            <p className="text-sm text-[#2a2e30]">Invoiceable Status:</p>
                            <div className="px-3 py-1 bg-[#FF5733] text-white text-sm rounded">
                                {invoiceableStatus}
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <label className="text-sm text-[#2a2e30]">Invoice Date:</label>
                            <input
                                type="date"
                                value={invoiceDate}
                                onChange={(e) => setInvoiceDate(e.target.value)}
                                className="px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:border-[#FF5733] focus:ring-1 focus:ring-[#FF5733]"
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="w-full max-w-7xl mx-auto px-2 sm:px-4 pb-8">

                {/* Tab Navigation */}
                <div className="bg-white rounded-2xl shadow-xl p-6">
                    <div className="mb-6 flex border-b border-gray-200">
                        <button
                            onClick={() => setActiveTab('uninvoiced')}
                            className={`px-6 py-3 font-semibold text-base transition-all border-b-2 ${activeTab === 'uninvoiced'
                                ? 'border-[#FF5733] text-[#FF5733]'
                                : 'border-transparent text-gray-600 hover:text-gray-900'
                                }`}
                        >
                            Uninvoiced Job Cards
                        </button>
                        <button
                            onClick={() => setActiveTab('posted')}
                            className={`px-6 py-3 font-semibold text-base transition-all border-b-2 ${activeTab === 'posted'
                                ? 'border-[#FF5733] text-[#FF5733]'
                                : 'border-transparent text-gray-600 hover:text-gray-900'
                                }`}
                        >
                            Posted Invoices
                        </button>
                    </div>

                    {/* Uninvoiced Tab */}
                    {activeTab === 'uninvoiced' && (
                        <div className="space-y-4">
                            {/* Filters */}
                            <Card className="border-0 shadow-md">
                                <CardBody className="p-4">
                                    <div className="flex flex-col sm:flex-row gap-4 items-end">
                                        <div className="flex-1 min-w-xs">
                                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                                Filter by Owner
                                            </label>
                                            <ReactSelect
                                                placeholder="All Owners"
                                                classNamePrefix="react-select"
                                                isClearable
                                                options={owners}
                                                value={ownerFilter}
                                                onChange={setOwnerFilter}
                                            />
                                        </div>
                                        <Button
                                            size="sm"
                                            onClick={fetchUninvoicedCards}
                                            className="bg-gray-600 hover:bg-gray-700"
                                        >
                                            <FontAwesomeIcon icon={faRefresh} className="mr-2" />
                                            Refresh
                                        </Button>
                                    </div>
                                </CardBody>
                            </Card>

                            {/* Summary Cards */}
                            {summaryItems.length > 0 && (
                                <Card className="border-0 shadow-md bg-blue-50">
                                    <CardBody className="p-4">
                                        <Typography variant="small" className="font-semibold text-gray-700 mb-3">
                                            Selection Summary
                                        </Typography>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                            {summaryItems.map(([ownerCode, data]) => (
                                                <div key={ownerCode} className="bg-white p-3 rounded-lg border border-blue-200">
                                                    <p className="text-sm font-semibold text-gray-800">
                                                        {data.ownerName || ownerCode}
                                                    </p>
                                                    <p className="text-xs text-gray-600">
                                                        {data.count} card{data.count !== 1 ? 's' : ''} • KES {data.total.toFixed(2)}
                                                    </p>
                                                </div>
                                            ))}
                                        </div>
                                    </CardBody>
                                </Card>
                            )}

                            {/* Job Cards Table */}
                            <Card className="border-0 shadow-md">
                                <CardBody className="p-0">
                                    {loading ? (
                                        <div className="p-6 text-center">
                                            <Typography>Loading job cards...</Typography>
                                        </div>
                                    ) : jobCards.length === 0 ? (
                                        <div className="p-6 text-center">
                                            <Typography className="text-gray-600">
                                                No uninvoiced job cards found with status "{invoiceableStatus}"
                                            </Typography>
                                        </div>
                                    ) : (
                                        <div className="overflow-x-auto">
                                            <table className="w-full text-sm">
                                                <thead className="bg-gray-50 border-b border-gray-200">
                                                    <tr>
                                                        <th className="px-4 py-3 text-left">
                                                            <Checkbox
                                                                checked={selectedCardIds.size === jobCards.length && jobCards.length > 0}
                                                                onChange={toggleSelectAll}
                                                            />
                                                        </th>
                                                        <th className="px-4 py-3 text-left font-semibold text-gray-700">Job Card No</th>
                                                        <th className="px-4 py-3 text-left font-semibold text-gray-700">Plate No</th>
                                                        <th className="px-4 py-3 text-left font-semibold text-gray-700">Owner</th>
                                                        <th className="px-4 py-3 text-left font-semibold text-gray-700">Completed Date</th>
                                                        <th className="px-4 py-3 text-right font-semibold text-gray-700">Total Cost</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {jobCards.map((card, idx) => (
                                                        <tr key={card.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                                            <td className="px-4 py-3">
                                                                <Checkbox
                                                                    checked={selectedCardIds.has(card.id)}
                                                                    onChange={() => toggleCardSelection(card.id)}
                                                                />
                                                            </td>
                                                            <td className="px-4 py-3 font-medium text-gray-800">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => navigate(`/job-cards/${card.id}`)}
                                                                    className="text-[#FF5733] font-semibold hover:underline"
                                                                >
                                                                    {card.job_card_no}
                                                                </button>
                                                            </td>
                                                            <td className="px-4 py-3 text-gray-700">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => card.plate_no && navigate(`/cars/${card.plate_no}`)}
                                                                    className={`font-semibold ${card.plate_no ? 'text-[#2a2e30] hover:underline' : 'text-gray-500 cursor-not-allowed'}`}
                                                                    disabled={!card.plate_no}
                                                                >
                                                                    {card.plate_no || '-'}
                                                                </button>
                                                            </td>
                                                            <td className="px-4 py-3 text-gray-700">
                                                                {card.owner_name} ({card.owner_code})
                                                            </td>
                                                            <td className="px-4 py-3 text-gray-700">
                                                                {card.completed_date ? new Date(card.completed_date).toLocaleDateString() : '-'}
                                                            </td>
                                                            <td className="px-4 py-3 text-right font-semibold text-gray-800">
                                                                KES {parseFloat(card.total_cost || 0).toFixed(2)}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </CardBody>
                            </Card>

                            {/* Action Button */}
                            {jobCards.length > 0 && (
                                <div className="flex justify-end">
                                    <Button
                                        onClick={handleBulkInvoice}
                                        disabled={selectedCardIds.size === 0 || loading}
                                        className="bg-brand-primary hover:bg-brand-primary/90 text-white"
                                    >
                                        <FontAwesomeIcon icon={faFileInvoiceDollar} className="mr-2" />
                                        Invoice Selected ({selectedCardIds.size})
                                    </Button>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Posted Invoices Tab */}
                    {activeTab === 'posted' && (
                        <div className="space-y-4">
                            {/* Refresh Button */}
                            <div className="flex justify-end">
                                <Button
                                    size="sm"
                                    onClick={fetchPostedInvoices}
                                    className="bg-gray-600 hover:bg-gray-700"
                                >
                                    <FontAwesomeIcon icon={faRefresh} className="mr-2" />
                                    Refresh
                                </Button>
                            </div>

                            {/* Posted Invoices Table */}
                            <Card className="border-0 shadow-md">
                                <CardBody className="p-0">
                                    {loading ? (
                                        <div className="p-6 text-center">
                                            <Typography>Loading posted invoices...</Typography>
                                        </div>
                                    ) : postedInvoices.length === 0 ? (
                                        <div className="p-6 text-center">
                                            <Typography className="text-gray-600">No posted invoices found</Typography>
                                        </div>
                                    ) : (
                                        <div className="overflow-x-auto">
                                            <table className="w-full text-sm">
                                                <thead className="bg-gray-50 border-b border-gray-200">
                                                    <tr>
                                                        <th className="px-4 py-3 text-left font-semibold text-gray-700">Invoice No</th>
                                                        <th className="px-4 py-3 text-left font-semibold text-gray-700">Owner</th>
                                                        <th className="px-4 py-3 text-left font-semibold text-gray-700">Plate No</th>
                                                        <th className="px-4 py-3 text-left font-semibold text-gray-700">Date</th>
                                                        <th className="px-4 py-3 text-right font-semibold text-gray-700">Amount</th>
                                                        <th className="px-4 py-3 text-left font-semibold text-gray-700">Status</th>
                                                        <th className="px-4 py-3 text-center font-semibold text-gray-700">Action</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {postedInvoices.map((inv, idx) => (
                                                        <tr key={inv.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                                            <td className="px-4 py-3 font-medium text-gray-800">
                                                                {inv.id}
                                                            </td>
                                                            <td className="px-4 py-3 text-gray-700">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => navigate(`/owners/statements/${inv.owner_code}`)}
                                                                    className="text-[#FF5733] font-semibold hover:underline"
                                                                >
                                                                    {inv.owner_code}
                                                                </button>
                                                            </td>
                                                            <td className="px-4 py-3 text-gray-700">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => inv.plate_no && navigate(`/cars/${inv.plate_no}`)}
                                                                    title={inv.notes || 'No notes available'}
                                                                    className={`font-semibold ${inv.plate_no ? 'text-[#2a2e30] hover:underline' : 'text-gray-500 cursor-not-allowed'}`}
                                                                    disabled={!inv.plate_no}
                                                                >
                                                                    {inv.plate_no || '-'}
                                                                </button>
                                                            </td>
                                                            <td className="px-4 py-3 text-gray-700">
                                                                {new Date(inv.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                                            </td>
                                                            <td className="px-4 py-3 text-right font-semibold text-gray-800">
                                                                KES {parseFloat(inv.amount || 0).toFixed(2)}
                                                            </td>
                                                            <td className="px-4 py-3">
                                                                <span className="inline-flex items-center px-2 py-1 text-xs font-semibold text-green-700 bg-green-100 rounded-full">
                                                                    Invoiced
                                                                </span>
                                                            </td>
                                                            <td className="px-4 py-3 text-center">
                                                                {inv.reversed ? (
                                                                    <span className="text-sm text-gray-500">Reversed</span>
                                                                ) : (
                                                                    <Button
                                                                        size="sm"
                                                                        color="red"
                                                                        variant="outlined"
                                                                        onClick={() => handleReverseClick(inv)}
                                                                    >
                                                                        <FontAwesomeIcon icon={faRotateLeft} className="mr-1" />
                                                                        Reverse
                                                                    </Button>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </CardBody>
                            </Card>
                        </div>
                    )}

                    {/* Results Modal */}
                    <Dialog open={showResultsModal} handler={() => setShowResultsModal(false)} size="lg">
                        <DialogHeader className="flex justify-between items-center">
                            <Typography variant="h6">Invoice Results</Typography>
                            <button onClick={() => setShowResultsModal(false)}>
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </DialogHeader>
                        <DialogBody>
                            {invoiceResults && (
                                <div className="space-y-4">
                                    {/* Status Summary */}
                                    <div className="grid grid-cols-2 gap-4">
                                        <Card className="bg-green-50 border border-green-200">
                                            <CardBody className="p-3">
                                                <Typography variant="small" className="text-green-700 font-semibold">
                                                    Successful
                                                </Typography>
                                                <Typography variant="h5" className="text-green-600">
                                                    {invoiceResults.filter(r => r.success).length}
                                                </Typography>
                                            </CardBody>
                                        </Card>
                                        <Card className="bg-red-50 border border-red-200">
                                            <CardBody className="p-3">
                                                <Typography variant="small" className="text-red-700 font-semibold">
                                                    Failed
                                                </Typography>
                                                <Typography variant="h5" className="text-red-600">
                                                    {invoiceResults.filter(r => !r.success).length}
                                                </Typography>
                                            </CardBody>
                                        </Card>
                                    </div>

                                    {/* Success Message */}
                                    {invoiceResults.every(r => r.success) && (
                                        <Card className="bg-blue-50 border border-blue-200">
                                            <CardBody className="p-4">
                                                <Typography variant="small" className="text-blue-700">
                                                    ✓ All job cards invoiced successfully!
                                                </Typography>
                                                <Typography variant="small" className="text-blue-600 mt-2">
                                                    • Invoices created for all {invoiceResults.length} job card(s)
                                                </Typography>
                                                <Typography variant="small" className="text-blue-600">
                                                    • Job cards automatically advanced to final stage
                                                </Typography>
                                                <Typography variant="small" className="text-blue-600">
                                                    • Timeline events created with invoice references
                                                </Typography>
                                            </CardBody>
                                        </Card>
                                    )}

                                    {/* Rollback Message */}
                                    {!invoiceResults.every(r => r.success) && (
                                        <Card className="bg-yellow-50 border border-yellow-200">
                                            <CardBody className="p-4">
                                                <Typography variant="small" className="text-yellow-700 font-semibold">
                                                    ⚠ Transaction Rolled Back
                                                </Typography>
                                                <Typography variant="small" className="text-yellow-600 mt-2">
                                                    One or more job cards failed to invoice. All invoicing, stage updates, and timeline entries have been rolled back to ensure data consistency.
                                                </Typography>
                                            </CardBody>
                                        </Card>
                                    )}

                                    {/* Failed Items */}
                                    {invoiceResults.some(r => !r.success) && (
                                        <div>
                                            <Typography variant="small" className="font-semibold text-red-700 mb-2">
                                                Failed Items:
                                            </Typography>
                                            <div className="space-y-2 max-h-64 overflow-y-auto">
                                                {invoiceResults
                                                    .filter(r => !r.success)
                                                    .map((result, idx) => (
                                                        <div key={idx} className="p-3 bg-red-50 border border-red-200 rounded text-sm">
                                                            <p className="font-semibold text-red-700">{result.job_card_id}</p>
                                                            <p className="text-red-600">{result.message}</p>
                                                        </div>
                                                    ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Successful Items Summary */}
                                    {invoiceResults.some(r => r.success) && (
                                        <div>
                                            <Typography variant="small" className="font-semibold text-green-700 mb-2">
                                                Successfully Invoiced ({invoiceResults.filter(r => r.success).length}):
                                            </Typography>
                                            <div className="space-y-2 max-h-48 overflow-y-auto">
                                                {invoiceResults
                                                    .filter(r => r.success)
                                                    .map((result, idx) => (
                                                        <div key={idx} className="p-3 bg-green-50 border border-green-200 rounded text-sm">
                                                            <p className="font-semibold text-green-700">{result.job_card_id}</p>
                                                            <p className="text-green-600">Invoice: {result.invoice_no} | Amount: KES {parseFloat(result.amount || 0).toFixed(2)}</p>
                                                        </div>
                                                    ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </DialogBody>
                        <DialogFooter>
                            <Button onClick={() => setShowResultsModal(false)} className="bg-gray-600">
                                Close
                            </Button>
                        </DialogFooter>
                    </Dialog>

                    {/* Reverse Transaction Dialog */}
                    {selectedTransaction && (
                        <ReverseTransactionDialog
                            open={reverseDialogOpen}
                            onClose={() => setReverseDialogOpen(false)}
                            transaction={selectedTransaction}
                            onReverseSuccess={handleReverseSuccess}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default JobCardInvoicing;
